import { useLayoutEffect, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { maybeLoadAnalytics, onConsentChange } from '@/lib/cookieConsent';

/**
 * ScrollToTop
 * Resets window scroll to (0,0) on every pathname change.
 * - Disables the browser's default scrollRestoration so back/forward doesn't preserve scroll.
 * - Uses useLayoutEffect + double-rAF to win against late-mounted animations / motion components
 *   that occasionally trigger layout-induced scroll preservation.
 * - Skips when the URL contains a hash (in-page anchor navigation).
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // First mount — attempt to load analytics if the user has already
    // granted consent in a previous session. Idempotent / safe no-op
    // when consent is absent.
    maybeLoadAnalytics();
    // React to consent decisions made during this session.
    const unsub = onConsentChange(() => { maybeLoadAnalytics(); });
    return () => { unsub(); };
  }, []);

  useLayoutEffect(() => {
    if (hash) return;
    const jump = () => {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    };
    jump();
    // Beat any post-mount scroll restoration from child route components.
    const r1 = requestAnimationFrame(() => {
      jump();
      requestAnimationFrame(jump);
    });
    return () => cancelAnimationFrame(r1);
  }, [pathname, hash]);

  // Per-route pageview tracking for Google Analytics (gtag.js).
  // Fires on every SPA navigation. Safe no-op if gtag is blocked / not loaded.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: pathname + (hash || ''),
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, hash]);

  return null;
}
